import { DateTime } from "luxon";
import { IntervalModel, SchoolClosure, SchoolHoursModel, ShortenedDays } from "model";

import {
  findSchoolClosureForInterval,
  generateSelectableDaysFromIntervals,
  newSelectableDay,
  SubScheduleDay,
} from "../utils";
import { isWeekday, isWeekend, mergeDateTime, roundUpToNearest15 } from "./dateTime";
import { newEndDateTime } from "./useRequestDate.utils";

// TODO: Should this be replaced with `isWeekday` since it's just a wrapper?
export const isDateSelectable = (date: string) => {
  return isWeekday(DateTime.fromISO(date));
};

/**
 * Returns the next weekday.
 *
 * This does not account for School Closure, use `getNextValidDate` instead.
 */
export const getNextWeekday = (dt: DateTime) => {
  switch (dt.weekday) {
    case 5:
      return dt.plus({ day: 3 });
    case 6:
      return dt.plus({ day: 2 });
    default:
      return dt.plus({ day: 1 });
  }
};

/**
 * Returns true if the given date does not fall on a weekend or a School Closure.
 *
 * @example
 * const date = "2025-01-01" // Wednesday
 * const schoolClosures = [
 *  // School Closure from Monday to Friday
 *  { start: "2024-12-30T07:30:00", end: "2025-01-03T16:30:00"}
 * ]
 *
 * isDateValid({ date, schoolClosures }) // false
 */
export function isDateValid(params: { date: DateTime; schoolClosures?: Array<SchoolClosure> }) {
  const { date, schoolClosures } = params;

  // Handle when the date is on a weekend
  if (isWeekend(date)) return false;

  // Handle when there are School Closures
  // And the date falls on a School Closure
  if (
    schoolClosures &&
    schoolClosures.length > 0 &&
    findSchoolClosureForInterval({
      // Note that we are using `startOf("day")` and `endOf("day")` since we
      // only support full day School Closure at the moment.
      interval: { start: date.startOf("day"), end: date.endOf("day") },
      schoolClosures: schoolClosures,
    })
  ) {
    return false;
  }

  return true;
}

/**
 * Returns the next valid date given the a date and a list of School Closures.
 *
 * @example
 * const date = "2025-01-04" // Saturday
 * const schoolClosures = [
 *  // The following Monday from 7:30 am to 4:30 pm.
 *  { start: "2025-01-06T07:30:00", end: "2025-01-06T16:30:00"}
 * ]
 *
 * getNextValidDate({ date, schoolClosures }); // "2025-01-07" Tuesday
 */
export function getNextValidDate(params: {
  date: DateTime;
  schoolClosures?: Array<SchoolClosure>;
  subSchedule?: Array<SubScheduleDay>;
}) {
  const { date, schoolClosures, subSchedule } = params;

  let currentDay = date.plus({ day: 1 });
  while (
    isWeekend(currentDay) ||
    (schoolClosures &&
      findSchoolClosureForInterval({
        interval: { start: currentDay.startOf("day"), end: currentDay.endOf("day") },
        schoolClosures: schoolClosures,
      })) ||
    (subSchedule && subSchedule.some((day) => isSameDate(day.start, currentDay.startOf("day"))))
  ) {
    currentDay = currentDay.plus({ day: 1 });
  }

  return currentDay;
}

export function isSameDate(a: DateTime, b: DateTime) {
  return a.toISODate() === b.toISODate();
}

/**
 * Returns true if both DateTime object represents the same date and time
 *
 * NOTE: We must coerce the DateTime object to a number to compare them.
 * See https://moment.github.io/luxon/#/math?id=comparing-datetimes
 */
export function isSameDateTime(a: DateTime, b: DateTime) {
  return +a === +b;
}

export const getMinEnd = (dt: DateTime) => {
  return dt.plus({ hours: 1 });
};

/**
 * Returns the start date time given the current time, a date and the school's
 * hours.
 *
 * TODO: This should only be used when generating a new Request, and not
 * when generating extra days in a existing request since it will skip
 * weekends and school closures.
 *
 * TODO: Rename in https://linear.app/swing-education/issue/MKTP-946/[swing-schools]-clean-up-unused-code
 */
export function newStartDateTime(params: {
  now: DateTime;
  date: DateTime;
  schoolHours?: SchoolHoursModel;
  schoolClosures?: Array<SchoolClosure>;
}) {
  const { now, date: _date, schoolHours, schoolClosures } = params;
  let date = _date;

  // If the proposed date is invalid
  if (isDateValid({ date, schoolClosures }) === false) {
    // Then default the start date to the next valid date
    const newDate = getNextValidDate({ date, schoolClosures });
    // And set the start time to the default school start time
    const newDateTime = getDefaultStartTime(newDate, schoolHours);

    return newDateTime;
  }

  // We only allow the start date time of a Request to be +1h from now rounded
  // to the nearest 15 minutes.
  const minStartDateTime = roundUpToNearest15(now.plus({ hour: 1 }));

  // Case: T < now +1h(~15m)
  // If the proposed start date is before this time
  if (date < minStartDateTime) {
    // Then update the new start date time to be within the minimum start date
    // time window
    const { day, hour, minute } = minStartDateTime;
    date = date.set({ day, hour, minute });
  }

  // Case: 10:59pm < T (today)
  // If the proposed start date (time) is today and it's past 10:59pm
  if (isSameDate(now, date) && now.set({ hour: 22, minute: 59 }).startOf("minute") < date) {
    // Then set the start date to the next weekday since we should not allow a
    // request to be less than 1 hour.
    return getDefaultStartTime(getNextWeekday(date), schoolHours);
  }

  // Otherwise, if the proposed is not today
  if (isSameDate(now, date) === false) {
    // Calculate the minimum start datetime for the given school day on this
    // date
    // Then set the start datetime is the latest time
    // NOTE: We are not fixing up date here since this is on purpose.
    return getDefaultStartTime(date, schoolHours);
  }

  // Case: now +1h(~15m) < T < 10:46pm
  // Return the latest time allowed, either the default hours or the current
  // time
  return DateTime.max(date, getDefaultStartTime(date, schoolHours));
}

// TODO: Merge this function with `getDefaultEndTime` in https://linear.app/swing-education/issue/MKTP-946
export const getDefaultStartTime = (
  date: DateTime,
  schoolHours: SchoolHoursModel = new SchoolHoursModel(),
) => {
  const shortDay = date.weekdayShort.toLowerCase() as ShortenedDays;
  const militaryTime = schoolHours[shortDay]?.start ?? schoolHours.getDefaultHours(shortDay).start;

  return date.set({
    hour: Math.floor(militaryTime / 100),
    minute: militaryTime % 100,
  });
};

// TODO: Merge this function with `getDefaultEndTime` in https://linear.app/swing-education/issue/MKTP-946
export const getDefaultEndTime = (
  date: DateTime,
  schoolHours: SchoolHoursModel = new SchoolHoursModel(),
) => {
  const shortDay = date.weekdayShort.toLowerCase() as ShortenedDays;
  const militaryTime = schoolHours[shortDay]?.end ?? schoolHours.getDefaultHours(shortDay).end;

  return date.set({
    hour: Math.floor(militaryTime / 100),
    minute: militaryTime % 100,
  });
};

const getLatestEndTime = (dt: DateTime, schoolHours?: SchoolHoursModel) => {
  const minEnd = getMinEnd(dt);
  const defaultEnd = getDefaultEndTime(dt, schoolHours);

  return DateTime.max(...[minEnd, defaultEnd]);
};

/** Returns the earliest date and time that a Request can start */
export function getSchoolEarliestStartDateTime(params: {
  /** The current datetime in the School's timezone */
  now: DateTime;
  schoolHours?: SchoolHoursModel;
}) {
  const { now, schoolHours } = params;

  // Allow same day requests up until 10:46 PM.
  if (now > now.set({ hour: 22, minute: 46 }).startOf("hour")) {
    // Then set the start day to the next week day
    const nextWeekday = getNextWeekday(now);
    // And set the start time to the default school's start time
    return getDefaultStartTime(nextWeekday, schoolHours);
  }

  // Otherwise, return the start time that is +1h from now at the next 15th
  // minute interval.
  return DateTime.max(
    roundUpToNearest15(now.plus({ hour: 1 })),
    getDefaultStartTime(now, schoolHours),
  );
}

export const getEndTime = (dt: DateTime, schoolHours?: SchoolHoursModel) => {
  return getLatestEndTime(dt, schoolHours);
};

type IsValidReqEndProps = {
  reqDate: DateTime;
  reqStartTime: DateTime;
  reqEndTime: DateTime;
};

export const isValidReqEnd = ({ reqDate, reqStartTime, reqEndTime }: IsValidReqEndProps) => {
  const normalizedStart = mergeDateTime(reqDate, reqStartTime);
  const normalizedEnd = mergeDateTime(reqDate, reqEndTime);
  const minEnd = getMinEnd(normalizedStart);
  const isRequestTooShort = minEnd > normalizedEnd;
  const isEndBeforeStart = normalizedEnd < normalizedStart;
  return !isRequestTooShort && !isEndBeforeStart;
};

// TODO: Is this still be used? Possibly clean up in the code cleanup ticket.
// https://linear.app/swing-education/issue/MKTP-946
export const isValidEndTime = (reqDate: DateTime, reqStartTime: DateTime, reqEndTime: DateTime) =>
  isValidReqEnd({
    reqDate: reqDate,
    reqStartTime: reqStartTime,
    reqEndTime: reqEndTime,
  });

export type TimesTableRowType = {
  // TODO: Remove this with code cleanup since this can be derived from either
  // the `startTime` or `endTime`.
  // https://linear.app/swing-education/issue/MKTP-946
  dateString: DateTime;

  startTime: DateTime;
  endTime: DateTime;
  isEnabled: boolean;

  // Renders a string for the SelectableDay component.
  label?: string;
};

export type TimesTableArrayType = TimesTableRowType[];

/**
 * Generates an array of TimesTableRowType objects for the RequestForm state.
 * It can handle the case where there ARE NO intervals or where the ARE
 * intervals.
 */
export function getInitialIntervals(params: {
  now: DateTime;
  schoolHours: SchoolHoursModel;
  schoolClosures?: Array<SchoolClosure>;
  intervals?: Array<IntervalModel>;
}) {
  const { now, schoolHours, schoolClosures, intervals } = params;

  // If there are no intervals
  if (!intervals || intervals.length === 0) {
    // Then return a single interval at the current/next valid date time.
    return [newTimesTableRowType({ now, date: now, schoolHours, schoolClosures })];
  }

  return generateSelectableDaysFromIntervals({
    now,
    intervals,
    schoolHours,
    schoolClosures,
  });
}

export const getIntervalsFromTimes = (times: IntervalModel[], schoolHours?: SchoolHoursModel) => {
  return generateExistingMultiDayTimesTable(times, schoolHours);
};

/**
 * Returns the inherited school hours for a given weekday.
 * Should only be used when the request is filled or waitlisted
 * AND the ADMIN_EXTEND_REQUEST_ENABLED flag is true.
 *
 * @param currentDate - The current date.
 * @param startDateTime - The start date time.
 * @param endDateTime - The end date time.
 */
export function getPreviousWeekdayTime({
  currentDate,
  selectableDays,
  schoolHours,
}: {
  currentDate: DateTime;
  selectableDays: TimesTableRowType[];
  schoolHours: SchoolHoursModel;
}) {
  const weekDayShort = currentDate.weekdayShort.toLowerCase() as ShortenedDays;
  const previousWeekday = [...selectableDays]
    .filter((day) => day.startTime < currentDate)
    .reverse()
    .find((day) => day.startTime.weekdayShort.toLowerCase() === weekDayShort);

  const inheritedStart = currentDate.set({
    hour: previousWeekday?.startTime.hour,
    minute: previousWeekday?.startTime.minute,
  });

  // If we do have a previous weekday, use the start time from the previous weekday
  const inheritedEnd = currentDate.set({
    hour: previousWeekday?.endTime.hour,
    minute: previousWeekday?.endTime.minute,
  });

  // If there is no last weekday, use the default start and end times
  const start = previousWeekday ? inheritedStart : getDefaultStartTime(currentDate, schoolHours);
  const end = previousWeekday ? inheritedEnd : getDefaultEndTime(currentDate, schoolHours);

  return { start, end };
}

/**
 * Returns a new list of SelectableDays.
 *
 * By default, missing days between the `startDate` and `endDate` range will
 * be enabled, however, the `disableMissingNonSchoolClosureDays` can be use
 * to disable them.
 *
 * This should use always when needing to generate a list of SelectableDays.
 */
export function generateSelectableDaysFromExistingSelectableDays(params: {
  now: DateTime;
  startDate: DateTime;
  endDate: DateTime;
  schoolHours: SchoolHoursModel;
  /** Optional **/
  schoolClosures?: Array<SchoolClosure>;
  selectableDays?: Array<TimesTableRowType>;
  /**
   * When true, disable days that are not in the original list and are not
   * School Closures.
   *
   * FIXME: Make this better.
   * This is the only usage of this flag since when we are initially generating
   * the missing days on page load. This is used for the Duplicate Request flow.
   * But afterwards, we want to enable them since it's a user action.
   */
  disableMissingNonSchoolClosureDays?: boolean;
  shouldUsePreviousWeekdayTimes?: boolean;
}) {
  const {
    now,
    startDate,
    endDate,
    selectableDays: oldSelectableDays = [],
    schoolHours,
    schoolClosures,
    disableMissingNonSchoolClosureDays = false,
    shouldUsePreviousWeekdayTimes,
  } = params;

  const oldSelectableDaysISODates = oldSelectableDays.map((interval) =>
    interval.dateString.toISODate(),
  );
  const newSelectableDays: Array<TimesTableRowType> = [];

  let currentDate = startDate;
  while (currentDate <= endDate) {
    const currentISODate = currentDate.toISODate();

    // If selectable day exist
    if (oldSelectableDaysISODates.includes(currentISODate)) {
      // Then copy it
      newSelectableDays.push(oldSelectableDays[oldSelectableDaysISODates.indexOf(currentISODate)]);
    }
    // Otherwise create a new selectable day
    else {
      // Find if there is a School Closure on the current date
      const currentSchoolClosure = findSchoolClosureForInterval({
        interval: {
          start: currentDate.startOf("day"),
          end: currentDate.endOf("day"),
        },
        schoolClosures,
      });

      // Determine the start date time
      const startDateTime = isSameDate(now, currentDate)
        ? // When the date is today
          // We must change the start time to be at least 1 hours from now
          getSchoolEarliestStartDateTime({ now, schoolHours })
        : // When the date is not today
          shouldUsePreviousWeekdayTimes
          ? // Re-use the previous weekday's time
            getPreviousWeekdayTime({ currentDate, selectableDays: oldSelectableDays, schoolHours })
              .start
          : // Use the default start time for the School
            getDefaultStartTime(currentDate, schoolHours);

      const endDateTime = shouldUsePreviousWeekdayTimes
        ? // Re-use the previous weekday's time
          getPreviousWeekdayTime({ currentDate, selectableDays: oldSelectableDays, schoolHours })
            .end
        : // Use the latest of: default end time OR start time + 1 hour
          getLatestEndTime(startDateTime, schoolHours);

      // Create a new selectable day
      const selectableDay = newSelectableDay({
        interval: {
          start: startDateTime,
          // The end time can be derived from the start time
          end: endDateTime,
        },
        // Disable when there is a School Closure on that date
        // And lastly disable when `disableMissingNonSchoolClosureDays` is true
        isEnabled: !currentSchoolClosure && !disableMissingNonSchoolClosureDays,
        // Add the School Closure label if it exists
        label: currentSchoolClosure?.reason,
      });

      newSelectableDays.push(selectableDay);
    }

    // Update the current date
    currentDate = getNextWeekday(currentDate);
  }

  // Handle situation where there is no overlap with the `oldSelectableDays`
  // If there are no enabled days for the new range
  if (newSelectableDays.filter((day) => day.isEnabled).length === 0) {
    // Then set the first day to be enabled
    newSelectableDays[0].isEnabled = true;
  }

  return newSelectableDays;
}

/**
 * Generates a single interval for a given date.
 *
 * TODO: Maybe rename to `generateNewDay` or something that is closer to what
 * it does vs the type is matches.
 */
export function newTimesTableRowType(params: {
  /** The current date time in the School's timezone */
  now: DateTime;
  /** The date to create the new interval */
  date: DateTime;
  /** The school hours */
  schoolHours: SchoolHoursModel;
  /** The school closures */
  schoolClosures?: Array<SchoolClosure>;
  /** Whether the interval is enabled */
  isEnabled?: boolean;
  /** The label for the interval in case it's a closure. */
  label?: string;
}) {
  const { now, date, schoolHours, schoolClosures, isEnabled = true, label } = params;

  const startDateTime = newStartDateTime({ now, date, schoolHours, schoolClosures });
  const endDateTime = newEndDateTime({ startDateTime, schoolHours });

  return {
    dateString: startDateTime,
    startTime: startDateTime,
    endTime: endDateTime,
    isEnabled,
    label,
  };
}

const generateDefaultNextDayTimesTableRow = (
  nextWeekDay: DateTime,
  schoolHours?: SchoolHoursModel,
  isEnabled?: boolean,
) => {
  return {
    dateString: nextWeekDay,
    startTime: getDefaultStartTime(nextWeekDay, schoolHours),
    endTime: getDefaultEndTime(nextWeekDay, schoolHours),
    isEnabled: isEnabled || false,
  };
};

const generateExistingMultiDayTimesTableRow = (interval: IntervalModel, isEnabled?: boolean) => {
  return {
    dateString: interval.start,
    startTime: interval.start,
    endTime: interval.end,
    isEnabled: isEnabled ?? false,
  };
};

const generateMissingDates = (
  curr: DateTime,
  endIndex: number,
  timeArrayCopy: IntervalModel[],
  multiDayArray: TimesTableRowType[],
  schoolHours?: SchoolHoursModel,
) => {
  if (endIndex >= timeArrayCopy.length) {
    return;
  }
  const nextWeekday = getNextWeekday(curr);
  if (isSameDate(nextWeekday, timeArrayCopy[endIndex].start)) {
    return;
  }
  multiDayArray.push(generateDefaultNextDayTimesTableRow(nextWeekday, schoolHours, false));
  generateMissingDates(nextWeekday, endIndex, timeArrayCopy, multiDayArray, schoolHours);
};

export const generateExistingMultiDayTimesTable = (
  timeArray: IntervalModel[],
  schoolHours?: SchoolHoursModel,
) => {
  const timeArrayCopy = [...timeArray];
  const multiDayArray: TimesTableRowType[] = [];

  timeArrayCopy.forEach((time, index) => {
    multiDayArray.push(generateExistingMultiDayTimesTableRow(time, true));
    const currentWorkingDay = time.start;
    generateMissingDates(currentWorkingDay, index + 1, timeArrayCopy, multiDayArray, schoolHours);
  });

  return multiDayArray;
};
